import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useMutation, useLazyQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { pipe, filter, either, not, pathEq } from 'ramda';

import { bookingShape, historyShape, userShape } from 'constants/propTypes';

import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import { Row, Column } from 'components/shared/Layout';
import { Spinner } from 'admin-components/components';
import Modal from 'components/ui/Modal/Modal';
import { bookingStatus, bookingStatusTypes } from 'types/booking';
import { sendDiscountCardData } from 'utils/loyaltyData';
import { correctlyFormatDate } from 'utils/dates';
import { getPassengersDeniedUKEntry } from 'utils/getPassengersDeniedUKEntry';

import discountCardsMap from 'types/discountCardsMap';
import PassengerFormTable from './PassengerFormTable';
import { injectIntl, FormattedMessage } from 'react-intl';
import { isUserEurostarAgent } from 'utils/user';
import { ADD_INFANT, DELETE_INFANT, VALIDATE_TICKETS, VOID_VALIDATION_TICKETS } from 'store/mutations';
import { TICKET_VALIDATION_FULL } from 'constants/ticket';
import { isGDSBooking } from 'utils/booking';
import { isGroupBooking } from 'utils/isGroupBooking';
import { types as passengerTypes } from 'types/passengers';
import { getInfantPassengersWithTravelDocumentStatus } from 'utils/getInfantPassengersFromDetails';
import {
  ELIGIBLE_LAP_PASSENGER_TYPES,
  INFANT_EDITABLE_BOOKING_STATUSES,
  INFANT_FIRST_NAME_KEY,
  INFANT_LAST_NAME_KEY,
  INFANT_UUID
} from './constants';

const IS_ELIGIBLE_TO_TRAVEL_PER_TRAVEL_DOCS = gql`
  query IsEligibleToTravelPerTravelDocs($reference: String!, $lastName: String!) {
    isEligibleToTravelPerTravelDocs(bookingReference: $reference, lastName: $lastName) {
      passengerUUID
      readyToTravel
    }
  }
`;

const UPDATE_PASSENGERS_AND_CONFIRM = gql`
  mutation UpdatePassengersAndConfirm($bookingReference: String!, $passengers: [InputPassenger!]!) {
    updatePassengersAndConfirm(bookingReference: $bookingReference, passengers: $passengers) {
      reference
      totalPriceToBePaid
      passengers {
        id
        type
        firstName
        lastName
        email
        phoneNumber
        birthDate
        loyaltyNumber
        disabilityType
      }
    }
  }
`;

const belongsToPassenger = pathEq(['passenger', 'id']);

const isCancelled =
  ({ allBookingItems, passengerId }) =>
  () =>
    allBookingItems
      .filter((item) => item.passengerId === passengerId)
      .every((item) => [bookingStatusTypes.CANCELLED, bookingStatusTypes.TEMPORARILY_CANCELLED].includes(item.status));

const isPassengerCheckedIn =
  ({ allBookingItems, passengerId }) =>
  () =>
    Boolean(allBookingItems.find((item) => item.passengerId === passengerId && item.isCheckedIn));

const canPassengerChangeName = ({ allBookingItems, passengerId }) => {
  const passengerItems = allBookingItems.filter(({ passengerId: legPassengerId }) => legPassengerId === passengerId);
  const nonCancelledItems = passengerItems.filter(({ status }) => status !== 'cancelled');
  // if there's a mix of cancelled and non-cancelled items for this passenger,
  // then use the canChangeName property from the non-cancelled items only
  const applicableItems = passengerItems.length === nonCancelledItems.length ? passengerItems : nonCancelledItems;
  return applicableItems.every(({ canChangeName }) => canChangeName);
};

const formatPassengersToInputPassenger = (passengers, discountCards, passengersBooking) =>
  passengers
    .filter(({ isPassengerEditable, isSuspend }) => isPassengerEditable === true && !isSuspend)
    .map(({ passengerId, firstName, lastName, email, phoneNumber, birthDate, loyaltyNumber, disabilityType }) => ({
      disabilityType,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      id: passengerId,
      discountCards: sendDiscountCardData(loyaltyNumber, passengerId, passengersBooking).concat(discountCards),
      email,
      phoneNumber: `00${phoneNumber}`,
      birthDate: birthDate ? correctlyFormatDate(birthDate) : null
    }));
const hasSNSNCBDBOrNSService = (outbound, inbound) =>
  [...outbound, ...inbound]
    .filter(({ cancelled }) => !cancelled)
    .some(({ serviceType }) => ['SN', 'SNCB', 'DB', 'NS'].includes(serviceType?.code));

const buildInfantRow = (passenger, infant) => ({
  firstName: infant[INFANT_FIRST_NAME_KEY] || '',
  parentPassengerId: passenger.passengerId,
  isInfant: true,
  isPassengerEditable: false,
  isTravelDocumentRequired: infant.isTravelDocumentRequired,
  hasTravelDocumentBeenProvided: infant.hasTravelDocumentBeenProvided,
  lastName: infant[INFANT_LAST_NAME_KEY] || '',
  passengerId: '',
  uuid: infant[INFANT_UUID],
  type: passengerTypes.INFANT_ONLY
});

const buildPassengersToDisplay = (passengers, infantPassengers) => {
  const infantsByPassengerRef = infantPassengers.reduce((acc, infant) => {
    acc[infant.passengerRef] = infant;

    return acc;
  }, {});

  return passengers.flatMap((passenger) => {
    const infant = infantsByPassengerRef[passenger.passengerId];

    if (!infant) {
      return [passenger];
    }

    return [passenger, buildInfantRow(passenger, infant)];
  });
};

const getAvailableInfantPassengers = (passengers, infantPassengers, allBookingItems) => {
  const usedPassengerRefs = new Set(infantPassengers.map(({ passengerRef }) => passengerRef));

  return passengers.filter(
    ({ passengerId, type }) =>
      ELIGIBLE_LAP_PASSENGER_TYPES.includes(type) && !usedPassengerRefs.has(passengerId) && !isCancelled({ allBookingItems, passengerId })()
  );
};

const PassengerForm = ({ booking, history, reference, isFormEditable, user = {}, intl }) => {
  const [isEligibleToTravel, { loading: isEligibleToTravelLoading, error: isEligibleToTravelError, data: isEligibleToTravelData }] =
    useLazyQuery(IS_ELIGIBLE_TO_TRAVEL_PER_TRAVEL_DOCS, {
      fetchPolicy: 'network-only'
    });

  useEffect(() => {
    try {
      if (booking.passengers[0].lastName?.length) {
        isEligibleToTravel({ variables: { reference: booking.reference, lastName: booking.passengers[0]?.lastName } });
      }
    } catch (error) {
      throw new AgentPortalError(error);
    }
  }, [booking, isEligibleToTravel]);

  const [updatePassengersAndConfirm, { loading: updatePassengersLoading, error: updatePassengersError }] =
    useMutation(UPDATE_PASSENGERS_AND_CONFIRM);
  const [validateTickets, { loading: validateTicketsLoading, error: validateTicketsError }] = useMutation(VALIDATE_TICKETS, {
    refetchQueries: ['booking']
  });
  const [voidValidationTickets, { loading: voidValidationTicketsLoading, error: voidValidationTicketsError }] = useMutation(
    VOID_VALIDATION_TICKETS,
    {
      refetchQueries: ['booking']
    }
  );
  const [addInfant, { error: addInfantError }] = useMutation(ADD_INFANT, {
    refetchQueries: ['booking']
  });
  const [deleteInfant, { error: deleteInfantError }] = useMutation(DELETE_INFANT, {
    refetchQueries: ['booking']
  });

  const {
    additionalDetails,
    status,
    displayStatus,
    allBookingItems,
    passengers,
    arePassengersEditable,
    productFamilyGroup,
    outbound,
    inbound,
    salesChannelCode,
    insurances = []
  } = booking;

  const passengersDeniedUKEntry = getPassengersDeniedUKEntry({ outbound, inbound });
  const [deniedUKEntryWarning, setDeniedUKEntryWarning] = useState(passengersDeniedUKEntry.length > 0);

  const error =
    updatePassengersError ||
    isEligibleToTravelError ||
    validateTicketsError ||
    voidValidationTicketsError ||
    addInfantError ||
    deleteInfantError;

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `/manage/bookings/${reference}/passengers`
    });
  }

  if (updatePassengersLoading || isEligibleToTravelLoading || validateTicketsLoading || voidValidationTicketsLoading) {
    return (
      <Row span={16} height="100vh">
        <Column justifyContent="center" alignItems="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  const hasSNTicket = hasSNSNCBDBOrNSService(outbound, inbound);
  const isPassengersEditable = isFormEditable && arePassengersEditable;
  const isGDS = isGDSBooking({ salesChannelCode });
  const isBookingConfirmed = status === bookingStatus.CONFIRMED;
  const isVoided = displayStatus === bookingStatus.VOIDED;
  const isBookingUnderpaid = status === bookingStatus.UNDERPAID;
  const hasActiveInsurances = insurances.some(({ cancelled, provisional }) => !cancelled && !provisional);

  const handleSubmit = async (payload) => {
    const discountCards = [];

    if (discountCardsMap[productFamilyGroup]) {
      discountCards.push(discountCardsMap[productFamilyGroup]);
    }

    const formattedPassengers = formatPassengersToInputPassenger(payload.passengers, discountCards, passengers);
    const {
      data: {
        updatePassengersAndConfirm: { totalPriceToBePaid }
      }
    } = await updatePassengersAndConfirm({
      variables: {
        bookingReference: reference,
        passengers: formattedPassengers
      }
    });

    const isBookingOnHold = [bookingStatus.TEMPORARILY_ON_HOLD, bookingStatus.ON_HOLD].includes(status);

    if (totalPriceToBePaid > 0 && !isBookingOnHold) {
      history?.push(`/manage/bookings/${reference}/payment`);
    }
  };

  const handleSuspendRestoreSubmit = ({ ticketsToConfirm = [], ticketsToSuspend = [] }) => {
    if (ticketsToConfirm?.length > 0) {
      voidValidationTickets({
        variables: {
          validations: ticketsToConfirm.map((ticketNumber) => ({
            ticketNumber,
            voidTypes: [TICKET_VALIDATION_FULL]
          }))
        }
      });
    }

    if (ticketsToSuspend?.length > 0) {
      validateTickets({
        variables: {
          validations: ticketsToSuspend.map((ticketNumber) => ({
            ticketNumber,
            device: user.username,
            validationType: TICKET_VALIDATION_FULL
          }))
        }
      });
    }
  };

  const isPaxDependant = (id) => additionalDetails.find((ad) => ad.passengerRef === id && ad.key === 'dependant' && ad.value === 'true');

  const passengersWithBookingItems = [...booking.outbound, ...booking.inbound]
    .flatMap(({ timing, bookingItems }) =>
      bookingItems.map((item) => ({
        ...item,
        timing
      }))
    )
    .reduce((acc, { passengerId, ...passengerDetails }) => {
      if (!acc[passengerId]) {
        acc[passengerId] = [passengerDetails];
      } else {
        acc[passengerId].push(passengerDetails);
      }

      return acc;
    }, {});

  const passengersToEdit = passengers
    .filter((pax) => allBookingItems.find((abi) => abi.passengerId === pax.id))
    .map(({ id, uuid, birthDate, ...rest }) => {
      const passengerJourneys = passengersWithBookingItems[id] ?? [];

      const confirmedTickets = [];
      const suspendedTickets = [];

      passengerJourneys.forEach(({ isSuspended, ticketNumber, timing, status }) => {
        if (isSuspended && status === bookingStatusTypes.SUSPENDED) {
          suspendedTickets.push(ticketNumber);
        } else if (status === bookingStatusTypes.CONFIRMED && moment(timing.isoDate).isAfter()) {
          confirmedTickets.push(ticketNumber);
        }
      });

      return {
        uuid,
        ...rest,
        // Renamed ID to passengerID to preserve it
        // because react hook forms does some magic see PassengerFormTable
        passengerId: id,
        isDependant: isPaxDependant(id),
        birthDate: birthDate && moment(birthDate).format('DD/MM/YYYY'),
        isTravelDocumentRequired: rest.travelDocumentsRequired?.some((doc) => doc.required),
        hasTravelDocumentBeenProvided: isEligibleToTravelData?.isEligibleToTravelPerTravelDocs?.find((doc) => doc.passengerUUID === uuid)
          ?.readyToTravel,
        isPassengerEditable:
          canPassengerChangeName({ allBookingItems, passengerId: id }) &&
          pipe(
            filter(belongsToPassenger(id)),
            either(isCancelled({ allBookingItems, passengerId: id }), isPassengerCheckedIn({ allBookingItems, passengerId: id })),
            not
          )(allBookingItems),
        confirmedTickets,
        suspendedTickets,
        isSuspend: suspendedTickets.length > 0,
        canSuspendRestore: confirmedTickets.length > 0 || suspendedTickets.length > 0
      };
    });

  const shouldShowSuspendRestoreCta = isUserEurostarAgent(user);
  const shouldEnableSuspendRestoreCta =
    passengersToEdit?.length > 0
      ? passengersToEdit.some(({ confirmedTickets, suspendedTickets }) => confirmedTickets?.length > 0 || suspendedTickets?.length > 0)
      : false && isBookingConfirmed;
  const infantPassengers = getInfantPassengersWithTravelDocumentStatus({
    additionalDetails,
    isEligibleToTravelPerTravelDocs: isEligibleToTravelData?.isEligibleToTravelPerTravelDocs,
    passengers: passengersToEdit
  });

  const availableInfantPassengers = getAvailableInfantPassengers(passengersToEdit, infantPassengers, allBookingItems);
  const canManageInfants = INFANT_EDITABLE_BOOKING_STATUSES.includes(status);

  const handleAddInfant = ({ passengerId, firstName, lastName }) =>
    addInfant({
      variables: {
        bookingReference: reference,
        passengerId,
        firstName: firstName.trim(),
        lastName: lastName.trim()
      }
    });

  const handleDeleteInfant = (passengerId) =>
    deleteInfant({
      variables: {
        bookingReference: reference,
        passengerId
      }
    });

  return (
    <>
      <Modal
        isOpen={deniedUKEntryWarning}
        onClose={() => setDeniedUKEntryWarning(false)}
        title={<FormattedMessage id="eta.passengers.modal.title" />}
        subtitle={<FormattedMessage id="eta.passengers.modal.warning" />}
      >
        {passengersDeniedUKEntry.map(({ passenger: p }) => (
          <>
            <FormattedMessage id={`eta.passengers.modal.passenger`} /> {p?.id?.split('_')?.pop()} ({p?.firstName} {p?.lastName})
            <br />
          </>
        ))}
      </Modal>
      <PassengerFormTable
        availableInfantPassengers={availableInfantPassengers}
        canManageInfants={canManageInfants}
        history={history}
        onAddInfant={handleAddInfant}
        onDeleteInfant={handleDeleteInfant}
        onSave={handleSubmit}
        isPassengersEditable={isPassengersEditable}
        user={user}
        hasSNTicket={hasSNTicket}
        intl={intl}
        passengers={buildPassengersToDisplay(passengersToEdit, infantPassengers)}
        isBookingConfirmed={isBookingConfirmed}
        isGDSBooking={isGDS}
        isVoided={isVoided || isBookingUnderpaid}
        onSuspendRestore={handleSuspendRestoreSubmit}
        shouldShowSuspendRestoreCta={shouldShowSuspendRestoreCta}
        shouldEnableSuspendRestoreCta={shouldEnableSuspendRestoreCta}
        hasActiveInsurances={hasActiveInsurances}
        isGroupBooking={isGroupBooking(productFamilyGroup)}
      />
    </>
  );
};

PassengerForm.propTypes = {
  user: userShape,
  history: historyShape,
  booking: bookingShape,
  reference: PropTypes.string.isRequired,
  isFormEditable: PropTypes.bool.isRequired,
  intl: PropTypes.shape({ formatMessage: PropTypes.func })
};

export default injectIntl(PassengerForm);
